/**
 * Date formatting utilities for Mochia.
 *
 * All functions operate in **local time** — Mochia is a personal finance
 * app and users expect "today" to mean their local calendar day. ISO date
 * strings of the form `YYYY-MM-DD` are interpreted as local-day boundaries
 * (not UTC midnight) to avoid the classic "off-by-one timezone" bug.
 */

import {
  endOfMonth as dfEndOfMonth,
  format as dfFormat,
  startOfMonth as dfStartOfMonth,
} from 'date-fns';

const CHINESE_WEEKDAYS = ['週日', '週一', '週二', '週三', '週四', '週五', '週六'] as const;

const ISO_LOCAL_DATE = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * Coerce a Date or ISO string to a Date. Bare `YYYY-MM-DD` strings are
 * interpreted as local midnight on that day (NOT UTC midnight). Strings
 * that include a time/zone are passed through to the Date constructor.
 */
function toDate(input: Date | string): Date {
  if (input instanceof Date) return input;
  const m = ISO_LOCAL_DATE.exec(input);
  if (m && m[1] !== undefined && m[2] !== undefined && m[3] !== undefined) {
    return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  return new Date(input);
}

/** `2026-05-02` — local-time YYYY-MM-DD. */
export function formatYmd(d: Date | string): string {
  const date = toDate(d);
  return dfFormat(date, 'yyyy-MM-dd');
}

/** `2026 年 5 月` (zh-TW default). */
export function formatMonthLabel(d: Date, locale: string = 'zh-TW'): string {
  if (locale === 'zh-TW' || locale === 'zh-Hant' || locale.startsWith('zh')) {
    return `${d.getFullYear()} 年 ${d.getMonth() + 1} 月`;
  }
  // Fall back to a generic ICU month-year format for non-zh locales.
  return new Intl.DateTimeFormat(locale, {
    year: 'numeric',
    month: 'long',
  }).format(d);
}

/** `5/2 (週六)` — month/day with Chinese weekday in parentheses. */
export function formatDayHeader(d: Date): string {
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const weekday = CHINESE_WEEKDAYS[d.getDay()];
  return `${month}/${day} (${weekday})`;
}

/**
 * True when both inputs land on the same local calendar day.
 * Accepts Date instances or ISO date strings (YYYY-MM-DD treated as local).
 */
export function isSameDay(a: Date | string, b: Date | string): boolean {
  const x = toDate(a);
  const y = toDate(b);
  return (
    x.getFullYear() === y.getFullYear() &&
    x.getMonth() === y.getMonth() &&
    x.getDate() === y.getDate()
  );
}

/** First day of the month at 00:00:00.000 local. */
export function startOfMonth(d: Date): Date {
  return dfStartOfMonth(d);
}

/** Last day of the month at 23:59:59.999 local. */
export function endOfMonth(d: Date): Date {
  return dfEndOfMonth(d);
}
